import { Flex } from 'antd';
import { Button, Heading, Switch, Text } from '../Ant';
import {
  FeatureList,
  WorkspaceSetupFields,
  WorkspaceSetupScroll,
} from '../../assets/styles/components/featureBox';

import { useEffect } from 'react';
import { usePage } from '../../context/PageContext';
import { useTranslation } from 'react-i18next';
import { LOCAL_STORAGE_NAMES } from '../../constants/constants';
import { useNavigate } from 'react-router-dom';
import { useFeatureSelection } from '../../hooks/useFeatureSelection';
import { useUser } from '../../context/UserContext';
import { LockFilled } from '../icons';
import StartModuleSelect from '../startModuleSelect/startModuleSelect';
import AppearanceSetupField from './appearanceSetupField';
import { getRouteAfterWorkspaceSetup } from '../../utils/startModule';

const FeatureSelection = () => {
  const { t } = useTranslation();
  const { setTitle } = usePage();
  const navigate = useNavigate();
  const { user } = useUser();
  const {
    features,
    featureStates,
    handleToggle,
    isAllEnabled,
    handleEnableAllToggle,
    activeFeatures,
    startModule,
    handleStartModuleChange,
    saveSelections,
  } = useFeatureSelection();

  useEffect(() => {
    setTitle(t('common:features.setUpYourWorkspace'));
  }, []);

  const handleContinue = () => {
    saveSelections();
    sessionStorage.removeItem(LOCAL_STORAGE_NAMES.isOnboarding);
    navigate(getRouteAfterWorkspaceSetup(startModule, user?.id));
  };
  return (
    <Flex vertical gap={15} className="h-full pb-gutter">
      <Flex vertical gap={2}>
        <Flex justify="space-between" align="center" gap={10}>
          <Heading
            data-testid="feature-list-heading"
            level={6}
            $fontsize="16px"
            $color="title"
            $weight={600}
            $lineheight="22px"
          >
            {t('common:features.featureLists')}
          </Heading>
          <Flex align="center" justify="end" gap={8}>
            <Text
              data-testid="enable-all-text"
              $fontsize="13px"
              $color="primary"
              $weight={500}
              $lineheight="18px"
            >
              {t('common:features.enableAll')}
            </Text>
            <Switch
              data-testid="enable-all-switch"
              aria-label={t('common:features.enableAll')}
              checked={isAllEnabled}
              onChange={handleEnableAllToggle}
            />
          </Flex>
        </Flex>
        <Text
          data-testid="feature-selection-note"
          $color="description"
          $lineheight="20px"
          $fontsize="13px"
        >
          {t('common:features.activateLater')}
        </Text>
      </Flex>
      <WorkspaceSetupScroll vertical gap={10} className="flex-grow min-h-0 overflow-y-auto">
        <FeatureList>
          {features.map((feature) => (
            <div
              data-testid={`feature-box-${feature?.text}`}
              key={feature.id}
              className={`feature-row ${feature.disabled ? 'always-on' : ''}`}
            >
              <Flex justify="space-between" align="center" className="w-full">
                <Flex gap={10} align="center">
                  {feature.icon}
                  <Text
                    data-testid={`feature-text-${feature?.text}`}
                    $fontsize="13px"
                    $color="title"
                    $weight={500}
                    $lineheight="20px"
                  >
                    {feature.text}
                  </Text>
                  {feature.disabled && (
                    <Flex
                      align="center"
                      gap={4}
                      className="always-on-badge"
                      data-testid={`feature-always-on-${feature?.text}`}
                    >
                      <LockFilled />
                      <Text $fontsize="11px" $weight={500} $color="primary">
                        {t('common:features.alwaysOn')}
                      </Text>
                    </Flex>
                  )}
                </Flex>
                <Switch
                  data-testid={`feature-switch-${feature?.text}`}
                  aria-label={feature.text}
                  disabled={feature.disabled}
                  checked={featureStates[feature.id] ?? false}
                  onChange={() => handleToggle(feature.id)}
                  $locked={feature.disabled}
                />
              </Flex>
            </div>
          ))}
        </FeatureList>
        <WorkspaceSetupFields gap={10} data-testid="workspace-setup-fields">
          <StartModuleSelect
            options={activeFeatures}
            value={startModule}
            onChange={handleStartModuleChange}
          />
          <AppearanceSetupField />
        </WorkspaceSetupFields>
      </WorkspaceSetupScroll>
      <Button
        type="primary"
        size="large"
        data-testid="feature-selection-continue-button"
        block
        onClick={handleContinue}
        className="flex-shrink-0"
      >
        {t('common:continue')}
      </Button>
    </Flex>
  );
};

export default FeatureSelection;
